import { type ReactNode, type RefObject, useLayoutEffect, useState } from 'react';

export interface StageFit {
  scale: number;
  left: number;
  top: number;
}

/**
 * The 1920×1080 design, scaled to fit and centred: in the whole window on a TV, or in `frame` when
 * something shares the screen with it (the office preview's bar). A frame is measured again whenever
 * it changes size, where the browser can say so, and on every window resize regardless.
 */
export function useStageFit(frame?: RefObject<HTMLElement | null>): StageFit {
  const measure = (): StageFit => {
    const el = frame?.current;
    const w = el ? el.clientWidth : window.innerWidth;
    const h = el ? el.clientHeight : window.innerHeight;
    const scale = Math.min(w / 1920, h / 1080);
    return { scale, left: (w - 1920 * scale) / 2, top: (h - 1080 * scale) / 2 };
  };
  const [fit, setFit] = useState(measure);
  useLayoutEffect(() => {
    const onResize = () => setFit(measure());
    onResize(); // the frame exists only now
    window.addEventListener('resize', onResize);
    // Chrome 64 on: an older TV browser has no ResizeObserver, and no frame either.
    const observer = frame?.current && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (observer && frame?.current) observer.observe(frame.current);
    return () => {
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
    // `measure` reads the ref when it runs; the frame element itself never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return fit;
}

/** The Wall's frame before there is a board: the wordmark, and one line saying what is happening. */
export function BootScreen({ rail, children }: { rail: ReactNode; children: ReactNode }) {
  return (
    <div className="wall">
      <div className="rail">
        <span>{rail}</span>
      </div>
      <div className="w-stage pair-stage">
        <div className="brand-row">
          <div className="w-mark">
            <i />
          </div>
          <div className="wordmark">
            PALLET<span>·</span>LEVEL
          </div>
        </div>
        <div className="boot">{children}</div>
      </div>
    </div>
  );
}
